import { usePageSpaceKey } from '@confluence/page-context';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import SpaceIdentifiersQuery from './__generated__/SpaceIdentifiersQuery.graphql.ts';
import type {
	SpaceIdentifiersQuery as SpaceIdentifiersQueryType,
	SpaceIdentifiersQueryVariables,
} from './__types__/SpaceIdentifiersQuery.ts';

type SpaceIdentifiersType = {
	spaceId: string | undefined;
	spaceKey: string | undefined;
	loading: boolean;
};

/**
 * This hook should only be used in rare cases where the system space key is needed.
 * Please consider using useSpaceKey or useSpaceId instead.
 * @param spaceKey The space key to get the identifiers for. If not provided, the current space key will be used.
 * @returns The space id, system space key, and query loading state for the given space key.
 */
export const useSpaceIdentifiers = (spaceKey?: string): SpaceIdentifiersType => {
	const [pageContextSpaceKey] = usePageSpaceKey();

	// The given space key may be an alias, not the actual key.
	spaceKey = spaceKey ?? pageContextSpaceKey;

	const spaceIdentifiersQueryOptions = { variables: { spaceKey }, skip: !spaceKey };
	const { data, loading } = useQueryNoFurtherUpdateInterop<
		SpaceIdentifiersQueryType,
		SpaceIdentifiersQueryVariables
	>(SpaceIdentifiersQuery, spaceIdentifiersQueryOptions);

	return {
		spaceId: data?.space?.id || undefined,
		// The returned space key is the actual key, not an alias.
		spaceKey: data?.space?.key || undefined,
		loading,
	};
};
